import type { Messages } from '../catalogue.en'

/**
 * Access administration in the Control Center: user accounts, roles and
 * capabilities. Mirrors `en/controlCenterAccess.ts`.
 *
 * Composed into the public catalogue by `catalogue.ru.ts`. Nothing
 * imports this module directly: consumers ask the catalogue, so how the
 * messages are stored stays this layer's business.
 */
export const controlCenterAccess: Partial<Messages> = {
  'workplaces.title': 'Места работы',
  'workplaces.classificationSettings': 'Профиль классификации мест работы',
  'workplaces.profile': 'Национальный профиль',
  'workplaces.changeProfile': 'Изменить профиль классификации',
  'workplaces.classificationUnsaved': 'Профиль классификации мест работы организации',
  'workplaces.empty': 'Места работы не указаны.',
  'workplaces.loadFailed': 'Не удалось загрузить места работы.',
  'workplaces.add': 'Добавить место работы',
  'workplaces.remove': 'Удалить место работы',
  'workplaces.defaultBranch': 'Основной филиал новой организации',
  'workplaces.edit': 'Изменить место работы',
  'workplaces.save': 'Сохранить место работы',
  'workplaces.branch': 'Филиал',
  'workplaces.profession': 'Профессия',
  'workplaces.position': 'Должность',
  'workplaces.specialty': 'Специальность',
  'workplaces.active': 'Место работы действует',
  'workplaces.available': 'Доступно',
  'workplaces.unavailable': 'Недоступно',
  'workplaces.from': 'Действует с',
  'workplaces.until': 'Действует до',
  'workplaces.il': 'Минздрав Израиля',
  'workplaces.ru': 'Минздрав России',
  'workplaces.convert': 'Применить профиль организации и выбрать новые значения',
  'workplaces.unsaved': 'Данные места работы',
  'workplaces.failed': 'Места работы недоступны. Введённые данные сохранены в форме.',
  'workplaces.changed': 'Место работы изменилось. Загрузите сохранённую редакцию перед сохранением.',
  'workplaces.profileChanged': 'Профиль классификации организации изменился. Обновите данные перед сохранением.',
  'workplaces.duplicate': 'Такое место работы уже действует в этом периоде.',
  'workplaces.futureBookings': 'Перенесите или отмените затронутые будущие записи на приём перед изменением места работы.',
  'workplaces.affectedBookings': 'Затронутые записи на приём',
  'workplaces.bookingCount': 'Затронутые записи на приём: {count}',
  'workplaces.bookingLink': 'Открыть запись на приём {number} в новой вкладке',
  'workplaces.codingInvalid': 'Выбранный код не относится к этому полю места работы.',
  'workplaces.branchUnavailable': 'Этот филиал недоступен для действующего места работы.',
  'workplaces.invalid': 'Проверьте поля и срок действия места работы.',
  'workplaces.forbidden': 'У вас нет прав на это изменение места работы.',
  'workplaces.one': 'Место работы',
  'workplaces.subtitle': 'В каком филиале работает врач и по какой классификации',
  'workplaces.employee': 'Сотрудник',
  'workplaces.state': 'Состояние',
  'workplaces.closed': 'Завершено',
  'workplaces.create': 'Новое место работы',
  'workplaces.saved': 'Место работы сохранено',
  'workplaces.loading': 'Загрузка мест работы',
  'workplaces.reloadFailed': 'Не удалось загрузить сохранённую редакцию.',
  'workplaces.readOnly': 'Это место работы доступно вам только для чтения.',
  'workplaces.open': 'Открыть раздел «Места работы»',
  'workplaces.current': 'Текущее',
  'workplaces.remembered': 'В прошлый раз',
  'workplaces.rememberFailed': 'Не удалось сохранить выбор места работы. Попробуйте ещё раз.',
  'workplaces.enterWithout': 'Войти без места работы',
  'workplaces.editClassification': 'Изменить классификацию',
  'organizationSetup.title': 'Создание организаций',
  'organizationSetup.create': 'Создать организацию',
  'organizationSetup.empty': 'Заявок на создание организаций нет.',
  'organizationSetup.failed': 'Создание организации недоступно. Введённые данные сохранены.',
  'organizationSetup.changed': 'Эта заявка изменилась. Обновите её состояние перед продолжением.',
  'organizationSetup.inputConflict': 'Этот запрос уже сохранён с другими данными. Закройте форму и откройте сохранённую заявку.',
  'organizationSetup.closed': 'Это действие больше недоступно для данной заявки.',
  'organizationSetup.forbidden': 'У вас нет прав на управление созданием организаций.',
  'organizationSetup.ownerEmail': 'Почта владельца',
  'organizationSetup.unsaved': 'Данные новой организации',
  'organizationSetup.unsavedLink': 'Нескопированная ссылка приглашения владельца',
  'organizationSetup.discard': 'Удалить введённые данные организации?',
  'organizationSetup.discardLink': 'Персональная ссылка приглашения больше не будет показана. Закрыть?',
  'organizationSetup.cancel': 'Отменить создание',
  'organizationSetup.cancelConfirm': 'Отменить создание организации и отозвать приглашение владельца?',
  'organizationSetup.refresh': 'Обновить состояние создания',
  'organizationSetup.initialize': 'Продолжить подготовку',
  'organizationSetup.initializing': 'Подготовка...',
  'organizationSetup.busy': 'Подготовка уже выполняется. Обновите её состояние перед повторной попыткой.',
  'organizationSetup.refreshBeforeRetry': 'Перед повторной попыткой обновите сохранённое состояние.',
  'organizationSetup.protocolsFailed': 'Не удалось установить протоколы. Завершённые шаги сохранены.',
  'organizationSetup.printFormsFailed': 'Не удалось установить печатные формы. Завершённые шаги сохранены.',
  'organizationSetup.langfuseFailed': 'Не удалось проверить проект промптов и телеметрии.',
  'organizationSetup.admissionFailed': 'Не удалось завершить настройку организации. Завершённые шаги сохранены.',
  'organizationSetup.awaitingLangfuse': 'Ожидается регистрация проекта Langfuse агентом настройки.',
  'organizationSetup.reloadFailed': 'Не удалось обновить заявку. Показанное состояние не изменилось.',
  'organizationSetup.protocols': 'Протоколы',
  'organizationSetup.printForms': 'Печатные формы',
  'organizationSetup.langfuse': 'Промпты и телеметрия',
  'organizationSetup.ownerAccepted': 'Вы стали владельцем. Организация подготавливается и пока недоступна для клинической работы.',
  'organizationSetup.state.awaiting_owner': 'Ожидает владельца',
  'organizationSetup.state.initializing': 'Подготовка организации',
  'organizationSetup.state.failed': 'Требуется завершить настройку',
  'organizationSetup.state.ready': 'Готова',
  'organizationSetup.state.cancelled': 'Отменена',
  'accountEmail.title': 'Подтверждённая почта аккаунта',
  'accountEmail.address': 'Адрес почты',
  'accountEmail.add': 'Добавить почту',
  'accountEmail.request': 'Отправить код',
  'accountEmail.code': 'Код подтверждения',
  'accountEmail.confirm': 'Подтвердить почту',
  'accountEmail.resend': 'Отправить новый код',
  'accountEmail.refresh': 'Обновить состояние почты',
  'accountEmail.submitted': 'Письмо с кодом передано на отправку.',
  'accountEmail.uncertain': 'Отправка письма не подтверждена. Проверьте почту перед запросом нового кода.',
  'accountEmail.verified': 'Почта подтверждена.',
  'accountEmail.failed': 'Подтверждение почты недоступно. Введённые данные сохранены.',
  'accountEmail.cooldown': 'Подождите минуту перед запросом нового кода.',
  'accountEmail.missing': 'Этот запрос больше не актуален. Обновите состояние или запросите новый код.',
  'accountEmail.expired': 'Срок действия кода истёк или попытки закончились. Запросите новый код.',
  'accountEmail.invalid': 'Неверный код подтверждения.',
  'accountEmail.occupied': 'Эта подтверждённая почта принадлежит другому аккаунту. Войдите в него; аккаунты не объединяются.',
  'accountEmail.throttled': 'Слишком много запросов. Повторите через пятнадцать минут.',
  'accountEmail.unsaved': 'Подтверждение почты аккаунта',
  'users.invites.title': 'Приглашения',
  'users.invites.create': 'Пригласить пользователя',
  'users.invites.recipient': 'Почта получателя',
  'users.invites.channel': 'Канал приглашения',
  'users.invites.email': 'Электронная почта',
  'users.invites.telegram': 'Telegram',
  'users.invites.telegramRecipient': 'Получатель в Telegram',
  'users.invites.web': 'Ссылка',
  'users.invites.webRecipient': 'Получатель по ссылке',
  'users.invites.kind.employee': 'Сотрудник',
  'users.invites.kind.developer': 'Разработчик',
  'users.invites.kind.first_owner': 'Первый владелец',
  'users.invites.kind.owner': 'Владелец',
  'users.invites.state.pending': 'Ожидает принятия',
  'users.invites.state.expired': 'Срок истёк',
  'users.invites.state.accepted': 'Принято',
  'users.invites.state.revoked': 'Отозвано',
  'users.invites.delivery': 'Отправка',
  'users.invites.delivery.pending': 'Не отправлено',
  'users.invites.delivery.submitted': 'Передано почтовому серверу',
  'users.invites.delivery.uncertain': 'Результат отправки неизвестен',
  'users.invites.delivery.refused': 'Отправка отклонена',
  'users.invites.personalLink': 'Персональная ссылка Telegram',
  'users.invites.webLink': 'Ссылка приглашения',
  'users.invites.expires': 'Действует до',
  'users.invites.empty': 'Приглашений нет',
  'users.invites.resend': 'Выпустить новую ссылку',
  'users.invites.revoke': 'Отозвать приглашение',
  'users.invites.resendConfirm': 'Прежняя ссылка перестанет работать. Новое приглашение будет действовать три дня.',
  'users.invites.revokeConfirm': 'По этому приглашению больше нельзя будет вступить в организацию. Существующее членство не изменится.',
  'users.invites.copy': 'Скопировать ссылку приглашения',
  'users.invites.copied': 'Ссылка скопирована',
  'users.invites.copyNamed': 'Скопировать ссылку приглашения: {name}, действует до {expires}',
  'users.invites.copyFailed': 'Не удалось скопировать ссылку.',
  'users.invites.linkUnavailable': 'Ссылку нельзя скопировать. Выпустите новую ссылку — прежняя перестанет работать.',
  'users.invites.unsaved': 'Открыто неотправленное приглашение пользователя.',
  'users.invites.changed': 'Приглашение изменилось. Проверьте его состояние перед следующей операцией.',
  'users.invites.branchInvalid': 'Выбранные филиалы больше недоступны. Проверьте филиалы организации.',
  'users.invites.notReady': 'Настройка организации не завершена.',
  'users.invites.telegramUnavailable': 'Бот сотрудников Telegram недоступен. Приглашение могло сохраниться без ссылки.',
  'users.invites.throttled': 'Слишком много попыток приглашения. Повторите позже.',
  'users.invites.invalid': 'Проверьте получателя, роли и места работы.',
  'users.invitation.title': 'Приглашение сотрудника',
  'users.invitation.missing': 'Ссылка приглашения недействительна. Запросите новую ссылку у организации.',
  'users.invitation.unavailable': 'Сервис приглашений недоступен. Последнее действие могло не завершиться; проверьте приглашение перед повторной попыткой.',
  'users.invitation.identityConflict': 'Этот email или Telegram уже связан с другим аккаунтом. Войдите в тот аккаунт.',
  'users.invitation.signInRequired': 'Войдите в аккаунт, связанный с этим приглашением.',
  'users.invitation.accountConflict': 'Выйдите из текущего аккаунта перед созданием другого.',
  'users.invitation.accountKind': 'Это приглашение не подходит для данного типа аккаунта.',
  'users.invitation.alreadyMember': 'У этого аккаунта уже есть доступ к организации.',
  'users.invitation.restoreRequired': 'Членство отключено. Подтвердите восстановление, чтобы принять новое приглашение.',
  'users.invitation.closed': 'Это приглашение больше не действует.',
  'users.invitation.changed': 'Приглашение или настройки организации изменились. Запросите новое приглашение.',
  'users.invitation.credentialsRequired': 'Выберите логин и пароль, соответствующий требованиям.',
  'users.invitation.invalidInput': 'Проверьте введённые данные аккаунта и имени.',
  'users.invitation.telegramRequired': 'Откройте это приглашение заново из бота сотрудников в Telegram для подтверждения личности.',
  'users.invitation.telegramUnknown': 'С этим Telegram ещё не связан аккаунт. Создайте аккаунт или войдите в существующий.',
  'users.invitation.passwordRequired': 'Перед принятием приглашения смените временный пароль.',
  'users.invitation.forbidden': 'Принять приглашение с текущим аккаунтом или доступом к приложению нельзя.',
  'users.invitation.expired': 'Срок приглашения истёк. Запросите новую ссылку у организации.',
  'users.invitation.revoked': 'Организация отозвала это приглашение.',
  'users.invitation.joined': 'Членство подтверждено.',
  'users.invitation.disabled': 'Это приглашение уже принято, но членство сейчас отключено.',
  'users.invitation.accepted': 'Приглашение уже принято. Войдите в принявший его аккаунт, чтобы проверить текущий доступ.',
  'users.invitation.account': 'Аккаунт',
  'users.invitation.welcome': 'Добро пожаловать. Создайте аккаунт, чтобы вступить, — остальное вы настроите потом в своей карточке.',
  'users.invitation.useExisting': 'У меня уже есть аккаунт',
  'users.invitation.createInstead': 'Создать новый аккаунт',
  'users.invitation.addPassword': 'Добавить логин и пароль',
  'users.invitation.restore': 'Восстановить моё членство в этой организации',
  'users.invitation.working': 'Подождите...',
  'users.invitation.accept': 'Подтвердить вступление',
  'users.invitation.create': 'Создать аккаунт и вступить',
  'users.invitation.telegramSignIn': 'Войти через Telegram',
  'users.invitation.recheck': 'Проверить статус приглашения',
  'users.invitation.unsaved': 'В форме приглашения есть несохранённые данные аккаунта или профиля.',
  "users.title": "Сотрудники",
  'users.subtitle': 'Сотрудники организации, роли и членство.',
  'users.loading': 'Загрузка пользователей',
  'users.loadFailed': 'Не удалось загрузить пользователей',
  'users.forbidden': 'Вам не разрешено читать пользователей этой клиники',
  'users.tableLabel': 'Пользователи клиники',
  'users.empty': 'Нет видимых пользователей',
  'users.emptyDetail': 'Здесь появятся учётные записи, созданные для этой клиники.',
  'users.noMatches': 'Нет пользователей, соответствующих этим фильтрам',
  'users.noMatchesDetail': 'Расширьте или очистите фильтры столбцов, чтобы вернуться к полному списку пользователей.',
  'users.column.name': 'Имя',
  'users.column.username': 'Имя пользователя',
  'users.column.roles': 'Роли',
  'users.column.status': 'Статус',
  'users.column.kind': 'Вид',
  'users.column.contact': 'Контакт',
  'users.status.active': 'Активен',
  'users.status.disabled': 'Заблокирован',
  /* «Медработник», НЕ «Врач». The Kind column says whether an account
     provides care at all — a nurse, a physiotherapist and a psychologist are
     all practitioners — and «Врач» names one profession, which is narrower
     than the fact. It is also a role name: the Roles column beside it carries
     the clinic's own role display text, and a clinic whose doctor role is
     called «Врач» printed the same word twice on one row for two different
     questions. en says Practitioner and he says מטפל; both are the broad
     word, and this is now the third. */
  'users.badge.owner': 'Владелец',
  'users.badge.you': 'Вы',
  'users.badge.admin': 'Администратор',
  'users.actions': 'Действия',
  'users.editNamed': 'Изменить {name}',
  'users.sections': 'Разделы пользователя',
  'users.createTitle': 'Добавить пользователя',
  'users.username': 'Имя пользователя',
  'users.initialPassword': 'Первоначальный пароль',
  'users.initialPasswordHint': 'От новой учётной записи можно потребовать сменить его после входа.',
  'users.lifecycleStatus': 'Статус входа',
  'users.passwordChangeRequired': 'Требовать смену пароля при следующем входе',
  'users.roles': 'Роли',
  'users.transferOwnership': 'Передать владение',
  'users.transferTitle': 'Передача владения клиникой',
  'users.transferDescription': 'Сделать {name} владельцем клиники. Ваши обычные роли не изменятся.',
  'users.transferCancel': 'Отмена',
  'users.membershipStatus': 'Доступ к организации',
  'users.remove': 'Удалить',
  'users.removeNamed': 'Удалить {name}',
  'users.removeConfirm': 'Сотрудник исчезнет из списка пользователей этой организации. Удалить можно только сотрудника, у которого здесь нет медицинских записей и объектов, — иначе его можно только заблокировать. Созданные объекты и доступ к другим организациям сохранятся. Если других организаций не останется, общая учётная запись будет заблокирована.',
  'users.removeFailed': 'Не удалось удалить пользователя.',
  'users.removeMedical': 'Удалить нельзя: в этой организации есть медицинские записи или объекты этого сотрудника. Заблокируйте его вместо удаления.',
  'users.removed': 'Пользователь удалён из этой организации. Его учётная запись в других организациях не изменена.',
  'users.removedAccountBlocked': 'Пользователь удалён из этой организации. Других организаций не осталось, поэтому общая учётная запись заблокирована.',
  'users.membershipDisable': 'Заблокировать',
  'users.membershipRestore': 'Разблокировать',
  'users.membershipDisableNamed': 'Заблокировать {name}',
  'users.membershipRestoreNamed': 'Разблокировать {name}',
  'users.membershipDisableConfirm': 'Сотрудник не сможет работать в этой организации, пока его не разблокируют. Роли, история и доступ к другим организациям сохранятся.',
  'users.membershipRestoreConfirm': 'Сотрудник снова получит доступ к этой организации с прежними ролями.',
  'users.membershipUnsaved': 'Сохраните или отмените изменения перед изменением доступа.',
  'users.accessHint': 'Заблокированный сотрудник не может работать здесь, пока его не разблокируют. Удалить можно только сотрудника без медицинских записей в этой организации.',
  'users.transferConfirm': 'Передать владение',
  'users.transferring': 'Передача',
  'users.transferFailed': 'Не удалось передать владение клиникой.',
  'users.practitioner': 'Профиль врача',
  'users.practitionerHint': 'Используйте это только для клиницистов, которым нужен контекст врача в клинических процессах.',
  'users.profession': 'Профессия по Минздраву',
  'users.expertise': 'Специализация по Минздраву',
  'users.codingNone': 'Не указано',
  'users.create': 'Создать пользователя',
  'users.creating': 'Создание',
  'users.created': 'Пользователь создан',
  'users.saving': 'Сохранение',
  'users.changedConcurrently': 'Кто-то другой изменил этого пользователя, пока форма была открыта.',
  'users.usernameTaken': 'Это имя пользователя уже используется.',
  'users.createFailed': 'Не удалось создать пользователя.',
  'users.unsavedIntent': 'Форма пользователя \u2014 данные, которые не были сохранены',
  'users.saveForbidden': 'Вам не разрешено изменять пользователей.',
  'users.saveFailed': 'Не удалось сохранить пользователя.',
  'users.detailsReadOnly': 'Контакты этого сотрудника здесь изменить нельзя.',
  'users.rolesOwnerNote': 'Изменение обычных ролей не передаёт владение и не снимает полномочия владельца.',
  'users.rolesNone': 'Нет ролей для назначения',
  'users.saveRoles': 'Сохранить роли',
  'users.savingRoles': 'Сохранение ролей',
  'users.rolesSaved': 'Роли сохранены',
  'users.rolesChangedConcurrently': 'Кто-то другой изменил этого пользователя, пока роли были открыты. Перезагрузите, чтобы увидеть текущие роли; ваш выбор остаётся.',
  'users.rolesForbidden': 'Вам не разрешено изменять роли этого пользователя.',
  'users.rolesNotFound': 'Этого пользователя больше нет.',
  'users.rolesUnavailable': 'Сервер недоступен. Ваш выбор остаётся.',
  'users.rolesSaveFailed': 'Не удалось сохранить роли.',
  'users.reloadUser': 'Перезагрузить пользователя',
  'users.userReloaded': 'Перезагружено с сервера. Проверьте роли и сохраните снова.',
  'users.reloadFailed': 'Не удалось перезагрузить пользователя.',
  'users.backToUsers': 'Назад к пользователям',
  'users.openCard': 'Открыть карточку сотрудника',
  'users.changeRoles': 'Изменить роли',
  'users.branchesNone': 'Филиалы не назначены.',
  'users.branchesFromWorkplaces': 'Филиалы медработника определяются его местами работы.',
  'users.ownership': 'Владение',
  'users.ownershipHint': 'Владелец обладает всеми полномочиями в организации; владелец всегда один.',
  'users.card.title': 'Карточка сотрудника',
  'users.card.notFound': 'Этого сотрудника нет в справочнике организации.',
  'users.card.access': 'Доступ',
  'users.card.manageAccess': 'Изменить доступ',

  "roles.title": "Роли и права",
  'roles.subtitle': 'Общие для всей платформы наборы ролей, предоставляемые ими права и места их назначения.',
  'roles.loading': 'Загрузка ролей',
  'roles.loadFailed': 'Не удалось загрузить роли',
  'roles.forbidden': 'Вам не разрешено читать определения ролей',
  'roles.unavailable': 'Определения ролей сейчас недоступны',
  'roles.capabilitiesLoading': 'Загрузка каталога прав',
  'roles.capabilitiesFailed': 'Не удалось загрузить каталог прав',
  'roles.tableLabel': 'Определения ролей',
  'roles.empty': 'Ролей нет',
  'roles.emptyDetail': 'Определения ролей, возвращаемые сервером, появятся здесь.',
  'roles.noMatches': 'Нет ролей, соответствующих фильтрам',
  'roles.noMatchesDetail': 'Расширьте или очистите фильтры столбцов, чтобы вернуться к полному списку ролей.',
  'roles.column.name': 'Название',
  'roles.column.code': 'Код',
  'roles.column.kind': 'Тип',
  'roles.column.lifecycle': 'Состояние',
  'roles.column.grants': 'Права',
  'roles.column.assignments': 'Назначения',
  'roles.column.areas': 'Области',
  'roles.column.updated': 'Последнее изменение',
  'roles.kind.editable': 'Редактируемая роль',
  'roles.kind.sealed': 'Защищённая роль',
  'roles.kind.ownerAuthority': 'Полномочия владельца',
  'roles.lifecycle.active': 'Активна',
  'roles.lifecycle.retired': 'Выведена из использования',
  'roles.origin.builtIn': 'Встроенная',
  'roles.origin.custom': 'Пользовательская',
  'roles.origin.relation': 'Производная от отношения',
  'roles.unrecognized': '{value} (не распознано)',
  'roles.actions': 'Действия',
  'roles.editNamed': 'Изменить {name}',
  'roles.openNamed': 'Открыть {name}',
  'roles.createTitle': 'Новая роль',
  'roles.cloneTitle': 'Клонирование роли',
  'roles.clonedFrom': 'Права и описание скопированы из {name}. Назначения, происхождение и история не копируются.',
  'roles.code': 'Код роли',
  'roles.codeHint': 'Строчные латинские буквы, цифры и подчёркивания. После создания код изменить нельзя.',
  'roles.displayName': 'Название',
  'roles.description': 'Описание',
  'roles.reason': 'Причина изменения',
  'roles.reasonHint': 'Сохраняется в журнале аудита вместе с точными изменениями прав.',
  'roles.capabilities': 'Права',
  'roles.capabilitySearch': 'Поиск прав',
  'roles.capabilityNoMatches': 'Нет подходящих прав',
  'roles.keptGrants': 'Права, которые этот каталог не может предложить, сохраняются как есть',
  'roles.audience.product': 'Продукт',
  'roles.audience.developer': 'Разработчики',
  'roles.area.ai': 'ИИ',
  'roles.area.app': 'Приложение',
  'roles.area.authorization': 'Авторизация',
  'roles.area.clinic': 'Администрирование клиники',
  'roles.area.clinical': 'Клиническая работа',
  'roles.area.documents': 'Документы',
  'roles.area.finance': 'Финансы',
  'roles.area.imagingStudies': 'Лучевые исследования',
  'roles.area.laboratory': 'Лаборатория',
  'roles.area.messages': 'Сообщения',
  'roles.area.notifications': 'Уведомления',
  'roles.area.patientCases': 'Случаи пациентов',
  'roles.area.patientHospitalStays': 'Госпитализации',
  'roles.area.patientLeads': 'Обращения пациентов',
  'roles.area.patientMessages': 'Сообщения пациентов',
  'roles.area.patients': 'Пациенты',
  'roles.area.practitioners': 'Специалисты',
  'roles.area.printForms': 'Печатные формы',
  'roles.area.protocols': 'Протоколы',
  'roles.area.referenceData': 'Справочные данные',
  'roles.area.schedule': 'Расписание',
  'roles.area.tasks': 'Задачи',
  'roles.area.tracker': 'Трекер',
  'roles.scopeFor': 'Область для {name}',
  'roles.scope.none': 'Не предоставлено',
  'roles.scope.own': 'Своё',
  'roles.scope.related': 'Связанное',
  'roles.scope.clinic': 'Клиника',
  'roles.scope.platform': 'Платформа',
  'roles.grants': 'Действующие права',
  'roles.grantsNone': 'Эта запись ничего не предоставляет.',
  'roles.facts': 'Определение',
  'roles.fact.origin': 'Происхождение',
  'roles.fact.lifecycle': 'Состояние',
  'roles.fact.assignments': 'Назначенные учётные записи',
  'roles.fact.updated': 'Последнее изменение',
  'roles.changes': 'Изменения',
  'roles.noChanges': 'Изменений пока нет.',
  'roles.change.added': 'Добавлено',
  'roles.change.removed': 'Удалено',
  'roles.change.scopeChanged': 'Область изменена',
  'roles.change.name': 'Название',
  'roles.change.description': 'Описание',
  'roles.change.fromTo': '{from} → {to}',
  'roles.change.empty': 'Не задано',
  'roles.save': 'Сохранить роль',
  'roles.saving': 'Сохранение',
  'roles.saved': 'Роль сохранена',
  'roles.create': 'Создать роль',
  'roles.creating': 'Создание',
  'roles.created': 'Роль создана',
  'roles.clone': 'Клонировать',
  'roles.retire': 'Вывести роль из использования',
  'roles.retiring': 'Вывод из использования',
  'roles.retired': 'Роль выведена из использования',
  'roles.restore': 'Восстановить роль',
  'roles.restoring': 'Восстановление',
  'roles.restored': 'Роль восстановлена',
  'roles.impactTitle': 'Применить изменение во всех клиниках?',
  'roles.impactDescription': 'Их права изменятся сразу после сохранения роли. Учётных записей с этой ролью во всех клиниках: {count}.',
  'roles.impactConfirm': 'Сохранить и применить',
  'roles.cancel': 'Отмена',
  'roles.retireTitle': 'Вывести эту роль из использования?',
  'roles.retireDescription': 'Выведенную роль нельзя назначать. Её определение и история сохраняются, позже её можно восстановить.',
  'roles.retireConfirm': 'Вывести',
  'roles.restoreTitle': 'Восстановить эту роль?',
  'roles.restoreDescription': 'Роль снова станет доступной для назначения в новой редакции.',
  'roles.restoreConfirm': 'Восстановить',
  'roles.readOnly': 'Только чтение',
  'roles.sealedDefault': 'Эта роль защищена выпуском платформы, и здесь её изменить нельзя.',
  'roles.ownerAuthorityNote': 'Полномочия владельца следуют из отношения владения клиникой. Это не роль: её нельзя назначить и нельзя изменить здесь.',
  'roles.retiredNote': 'Эта роль выведена из использования и не может быть назначена. Восстановите её перед редактированием.',
  'roles.validation.code': 'Введите код роли',
  'roles.validation.name': 'Введите название',
  'roles.validation.grants': 'Предоставьте хотя бы одно право',
  'roles.validation.reason': 'Введите причину',
  'roles.error.notFound': 'Этой роли больше нет на сервере.',
  'roles.error.forbidden': 'Вам не разрешено изменять эту роль.',
  'roles.error.scopeInvalid': 'Сервер отклонил одну из областей права. Проверьте области и сохраните снова.',
  'roles.error.changedConcurrently': 'Кто-то другой изменил эту роль, пока редактор был открыт. Перезагрузите её, чтобы увидеть текущую редакцию; несохранённые правки остаются.',
  'roles.error.inUse': 'Эта роль всё ещё назначена, и вывести её из использования нельзя.',
  'roles.error.unavailable': 'Сервер недоступен. Несохранённые правки остаются.',
  'roles.error.invalid': 'Сервер отклонил это определение роли.',
  'roles.error.unknown': 'Сервер вернул неожиданный ответ. Несохранённые правки остаются.',
  'roles.reload': 'Перезагрузить роль',
  'roles.reloaded': 'Перезагружено с сервера. Проверьте изменения и сохраните снова.',
  'roles.reloadFailed': 'Не удалось перезагрузить роль.',
  'roles.unsavedIntent': 'Определение роли \u2014 данные, которые не были сохранены',
  'roles.detailLoading': 'Загрузка роли',
  'roles.detailFailed': 'Не удалось загрузить эту роль',
}
